import React, { useEffect, useState } from 'react';
import { IonButton, isPlatform } from '@ionic/react';
import { Swiper, SwiperSlide, useSwiper } from 'swiper/react';
import { Mousewheel, Pagination } from 'swiper/modules';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { UserOnboardingState } from './UserOnboardingState';
import { useSubscription } from '@meraki-internal/state';
import { useBreakpoints } from '../theme/useBreakpoints';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { Text } from '../components/Text';
import { AuthService } from '../auth/AuthService';
import { AppPrincipal, isAnonymousViewer } from '../auth/AppPrincipal';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { ISmartStorageProvider, LocalStorageProvider } from '../support/LocalStorageProvider';

import 'swiper/css';
import 'swiper/css/pagination';

interface IOnboardingSlide {
    heading: string;
    details: string[];
    image: string;
    buttonLabel: string;
}

type IDoneHandler = ({ hide, trackingFrom }: { hide: boolean, trackingFrom: string }) => Promise<void>;

type ISignIn = ({ trackingFrom }: { trackingFrom: 'Welcome Create Account' | 'Welcome Existing Account' }) => Promise<void>;

const Buttons: React.FC<{slide: IOnboardingSlide, signIn?: ISignIn, progress: number, onDone: IDoneHandler}> = ({ slide, signIn, progress, onDone }) => {
    const swiper = useSwiper();
    const onContinue = async () => {
        if (progress === 1) {
            onDone({ hide: true, trackingFrom: slide.buttonLabel });
        } else {
            swiper.slideNext();
        }
    };
    const onTryWithoutAccount = async () => {
        onDone({ hide: true, trackingFrom: 'Try Without Account' });
    };
    const onCreateAccount = async () => {
        await onDone({ hide: false, trackingFrom: 'Create Account' });
        await signIn!({ trackingFrom: 'Welcome Create Account' });
    };
    const onSignIn = async () => {
        await signIn!({ trackingFrom: 'Welcome Existing Account'});
    };
    return (
        <div style={{display: 'flex', flexDirection: 'column', width: '100%', padding: '0px 50px', maxWidth: 400, textAlign: 'center'}}>
            {!isPlatform('capacitor') && progress > 0 &&
                <IonButton fill="clear" type="button" onClick={() => swiper.slidePrev()}>&lt; Back</IonButton>
            }
            {progress === 0 && signIn &&
                <IonButton fill="clear" type="button" onClick={onSignIn}>I already have an account</IonButton>
            }
            {(progress === 1 && signIn) ? (
                <>
                    <IonButton fill="clear" type="button" onClick={onTryWithoutAccount}>Try without account</IonButton>
                    <IonButton expand="block" style={{'--border-radius': '16px'}} onClick={onCreateAccount}>Create account</IonButton>
                </>
            ) : (
                <IonButton expand="block" style={{'--border-radius': '16px'}} onClick={() => onContinue()}>{slide.buttonLabel}</IonButton>
            )}
        </div>
    );
};

const PhoneContent: React.FC<{slide: IOnboardingSlide, onDone: IDoneHandler, progress: number, signIn?: ISignIn}> = ({ slide, onDone, progress, signIn }) => {
    return (
        <div style={{display: 'flex', flexDirection: 'column', width: '100%', height: '100%', backgroundColor: 'white'}}>
            <div style={{width: '100%', height: '50%', textAlign: 'center', backgroundColor: '#9CD6EC', paddingTop: 'calc(var(--ion-safe-area-top) - 10px)'}}>
                <img style={{height: '100%', objectFit: 'cover'}} src={`./assets/images/welcome/${slide.image}`} alt="Caring" />
            </div>
            <div style={{width: '100%', height: '50%', maxWidth: 500, margin: '0 auto', borderTop: '1px solid var(--ion-color-step-100)'}}>
                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', height: '100%', width: '100%', padding: '70px 20px 40px'}}>
                    <div style={{textAlign: 'center'}}>
                        <div style={{fontSize: 24, fontWeight: 'bold'}}>{slide.heading}</div>
                        <div style={{padding: '20px 40px 0'}}>
                            {slide.details.map((detail, i) => (
                                <Text type="detail" key={`detail-${i}`}>{detail}</Text>
                            ))}
                        </div>
                    </div>
                    <Buttons slide={slide} progress={progress} onDone={onDone} signIn={signIn} />
                </div>
            </div>
        </div>
    );
};

const TabletContent: React.FC<{slide: IOnboardingSlide, onDone: IDoneHandler, progress: number, signIn?: ISignIn}> = ({ slide, onDone, progress, signIn }) => {
    return (
        <div style={{width: '100%', height: '100%', display: 'flex', alignItems: 'center', backgroundColor: 'white'}}>
            <div style={{width: '50%', height: '100%', display: 'flex', alignItems: 'center', backgroundColor: '#9CD6EC'}}>
                <img style={{width: '100%', maxWidth: 650, margin: '0 auto', objectFit: 'cover', backgroundColor: 'white', paddingTop: 30, clipPath: 'circle(35% at 50% 46%)'}} src={`./assets/images/welcome/${slide.image}`} alt="Caring" />
            </div>
            <div style={{width: '50%', maxWidth: 500, margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', height: 350}}>
                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 10}}>
                    <div style={{fontSize: 26, fontWeight: 'bold'}}>{slide.heading}</div>
                    <div style={{padding: '20px 40px 0', textAlign: 'center'}}>
                        <div style={{padding: '20px 40px 0'}}>
                            {slide.details.map((detail, i) => (
                                <Text type="detail" key={`detail-${i}`}>{detail}</Text>
                            ))}
                        </div>
                    </div>
                </div>
                <Buttons slide={slide} progress={progress} onDone={onDone} signIn={signIn} />
            </div>
        </div>
    );
};

export class WelcomeStorageProvider {
    completed: ISmartStorageProvider<boolean>;

    static inject = () => [LocalStorageProvider];
    constructor(storage: LocalStorageProvider) {
        this.completed = storage.getBooleanProvider('welcome-completed', { userNeutral: true });
    }
}

export const Welcome: React.FC = () => {
    const { isAboveBreakpoint } = useBreakpoints();

    const history = useInstance(HistoryViewModel);
    const mixpanel = useInstance(MixPanelEventEmitter);

    const principal = useInstance(AppPrincipal);
    const isAnonymous = isAnonymousViewer(principal);
    const authService = useInstance(AuthService);

    const { completed } = useInstance(WelcomeStorageProvider);

    const onboarding = useInstance(UserOnboardingState);
    useSubscription(() => onboarding);

    const [show, setShow] = useState(false);

    useEffect(() => {
        // TODO add tests for welcome slides
        if (localStorage.getItem('IS_AUTOMATED_TESTS') === 'true') {
            return;
        }
        // if signed in after completing anonymously, transfer flag from storage to dynamo
        if (!isAnonymous && completed.get()) {
            onboarding.completeStep('recording').then(completed.remove);
            return;
        }
        // don't show when deep linking anywhere other than home page
        if (!['/', '/caregiver'].includes(history.getCurrentLocation().pathname)) {
            return;
        }
        // don't show if flag set in local storage for anonymous user
        if (isAnonymous && completed.get()) {
            return;
        }
        // don't show if flag set in dynamo for signed-in user
        if (!isAnonymous && (!onboarding.isLoaded() || onboarding.isStepComplete('recording'))) {
            return;
        }
        // made it through all the checks, so show
        setShow(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onboarding.state?.loaded, onboarding.state?.steps.recording]);

    useEffect(() => {
        if (show) {
            mixpanel.track('Welcome Onboarding Opened');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    if (!show) {
        return null;
    }

    const onDone: IDoneHandler = async ({ hide, trackingFrom }) => {
        if (hide) {
            setShow(false);
        }
        if (isAnonymous) {
            // if anonymous, set flag in local storage
            completed.set(true);
        } else {
            // if signed in, set flag in dynamo
            await onboarding.completeStep('recording');
        }
        mixpanel.track('Welcome Flow Completed', () => ({ By: trackingFrom }));
    };

    const signIn = async ({ trackingFrom }: { trackingFrom: 'Welcome Create Account' | 'Welcome Existing Account' }) => {
        mixpanel.track('Sign In Initiated', () => ({ From: trackingFrom }));
        await authService.login();
    };

    const slides: IOnboardingSlide[] = [
        {
            heading: 'Catch Every Word',
            details: ['Care conversations move fast.', 'Innerhive takes notes so', 'you can stay present.'],
            image: '1-conversations.webp',
            buttonLabel: 'Tell me more...'
        },
        {
            heading: 'Notes with One Tap',
            details: ['No scribbling, no juggling pens—', 'just tap record, and we do the rest!.'],
            image: '2-notes.webp',
            buttonLabel: 'Sounds easy!'
        },
        {
            heading: 'Instant Summaries',
            details: ['Get clear highlights with next steps—', 'easy-to-read and ready to share.'],
            image: '3-summaries.webp',
            buttonLabel: 'I like clarity'
        },
        {
            heading: 'Care Together',
            details: ['Keep everyone engaged and', 'in the loop with automatic updates.'],
            image: '4-care.webp',
            buttonLabel: 'Let’s do it!'
        }
    ];

    const Content = isAboveBreakpoint('lg') ? TabletContent : PhoneContent;

    return (
        <div style={{position: 'absolute', width: '100%', height: '100%', paddingBottom: 'var(--ion-safe-area-bottom)'}}>
            <Swiper
                style={{height: '100%', width: '100%'}}
                modules={[Pagination, Mousewheel]}
                slidesPerView={1}
                centeredSlides
                mousewheel
                pagination={{ clickable: true }}
                onSlideChange={s => {
                    const event = (s.previousIndex < s.activeIndex ? 'Welcome Screen Advanced' : 'Welcome Screen Reversed');
                    mixpanel.track(event, () => ({
                        From: `${s.previousIndex + 1}: ${slides[s.previousIndex].heading}`,
                        To: `${s.activeIndex + 1}: ${slides[s.activeIndex].heading}}`
                    }));
                }}
            >
                {slides.map((slide, i) => (
                    <SwiperSlide key={`slide-${i}`} style={{width: '100', height: '100%'}}>
                        <Content
                            slide={slide}
                            onDone={onDone}
                            progress={i/(slides.length - 1)}
                            signIn={isAnonymous ? signIn : undefined}
                        />
                    </SwiperSlide>
                ))}
            </Swiper>
        </div>
    );
};
